<template>
  <v-container class="wo-ist-oli-container fill-height">
    <v-card class="mx-auto" max-width="1200">
      <v-card-title class="text-h4 text-center py-4">
        <v-icon size="32" class="mr-2">mdi-magnify</v-icon>
        Wo ist Oli?
      </v-card-title>

      <v-card-text>
        <div class="text-center mb-4">
          <v-chip color="primary" class="mr-2">
            <v-icon start>mdi-clock-outline</v-icon>
            {{ formatTime(elapsedTime) }}
          </v-chip>
          <v-chip color="success" class="mr-2">
            <v-icon start>mdi-trophy</v-icon>
            Gefunden: {{ foundCount }}/{{ totalOlis }}
          </v-chip>
          <v-chip v-if="level > 1" color="info">
            <v-icon start>mdi-chart-line</v-icon>
            Level {{ level }}
          </v-chip>
        </div>

        <v-alert v-if="gameWon" type="success" class="mb-4">
          <div class="text-h6">🎉 Glückwunsch!</div>
          <div>Du hast alle Olis in {{ formatTime(elapsedTime) }} gefunden!</div>
          <v-btn
            color="white"
            variant="outlined"
            class="mt-2"
            @click="nextLevel"
          >
            Nächstes Level
          </v-btn>
        </v-alert>

        <div
          ref="gameArea"
          class="game-area"
          :style="{ cursor: gameWon ? 'default' : 'crosshair' }"
        >
          <!-- Ablenkende Personen -->
          <div
            v-for="(person, index) in people"
            :key="`person-${index}`"
            class="person"
            :style="{
              left: person.x + 'px',
              top: person.y + 'px',
              fontSize: person.size + 'px',
            }"
          >
            {{ person.emoji }}
          </div>

          <!-- Die versteckten Olis (Biere!) -->
          <div
            v-for="(oli, index) in olis"
            :key="`oli-${index}`"
            class="oli"
            :class="{ found: oli.found, pulse: !oli.found }"
            :style="{
              left: oli.x + 'px',
              top: oli.y + 'px',
              fontSize: oli.size + 'px',
            }"
            @click="findOli(index)"
          >
            🍺
          </div>
        </div>

        <div class="text-center mt-4">
          <v-btn color="primary" @click="resetGame" prepend-icon="mdi-refresh">
            Neues Spiel
          </v-btn>
        </div>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'

const gameArea = ref<HTMLElement | null>(null)
const olis = ref<Array<{ x: number; y: number; found: boolean; size: number }>>([])
const people = ref<Array<{ x: number; y: number; emoji: string; size: number }>>([])
const foundCount = ref(0)
const level = ref(1)
const gameWon = ref(false)
const elapsedTime = ref(0)
const timerInterval = ref<number | null>(null)

const totalOlis = computed(() => 3 + level.value - 1) // Start mit 3, dann +1 pro Level

const emojis = [
  '👨', '👩', '🧑', '👴', '👵', '👨‍🦰', '👩‍🦰', '👨‍🦱', '👩‍🦱',
  '👨‍🦲', '👩‍🦲', '👨‍🦳', '👩‍🦳', '🧔', '👱‍♀️', '👱‍♂️',
  '👨‍⚕️', '👩‍⚕️', '👨‍🎓', '👩‍🎓', '👨‍🏫', '👩‍🏫',
  '👨‍⚖️', '👩‍⚖️', '👨‍🌾', '👩‍🌾', '👨‍🍳', '👩‍🍳',
  '👨‍🔧', '👩‍🔧', '👨‍🏭', '👩‍🏭', '👨‍💻', '👩‍💻',
  '👨‍🔬', '👩‍🔬', '👨‍🎨', '👩‍🎨', '👨‍🚀', '👩‍🚀',
]

const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

const startTimer = () => {
  elapsedTime.value = 0
  if (timerInterval.value) {
    clearInterval(timerInterval.value)
  }
  timerInterval.value = window.setInterval(() => {
    elapsedTime.value++
  }, 1000)
}

const stopTimer = () => {
  if (timerInterval.value) {
    clearInterval(timerInterval.value)
    timerInterval.value = null
  }
}

const initGame = () => {
  if (!gameArea.value) return

  const width = gameArea.value.clientWidth
  const height = gameArea.value.clientHeight
  const numberOfPeople = 30 + level.value * 10 // Mehr Personen pro Level

  olis.value = []
  people.value = []
  foundCount.value = 0
  gameWon.value = false

  // Olis platzieren
  for (let i = 0; i < totalOlis.value; i++) {
    olis.value.push({
      x: Math.random() * (width - 60),
      y: Math.random() * (height - 60),
      found: false,
      size: 30 + Math.random() * 20, // Verschiedene Größen
    })
  }

  // Ablenkende Personen platzieren
  for (let i = 0; i < numberOfPeople; i++) {
    people.value.push({
      x: Math.random() * (width - 60),
      y: Math.random() * (height - 60),
      emoji: emojis[Math.floor(Math.random() * emojis.length)],
      size: 25 + Math.random() * 25, // Verschiedene Größen
    })
  }

  startTimer()
}

const findOli = (index: number) => {
  if (olis.value[index].found || gameWon.value) return

  olis.value[index].found = true
  foundCount.value++

  // Confetti Effekt (visuell)
  const confetti = document.createElement('div')
  confetti.textContent = '✨'
  confetti.style.position = 'fixed'
  confetti.style.left = olis.value[index].x + 'px'
  confetti.style.top = olis.value[index].y + 'px'
  confetti.style.fontSize = '40px'
  confetti.style.pointerEvents = 'none'
  confetti.style.animation = 'float-up 1s ease-out'
  gameArea.value?.appendChild(confetti)
  setTimeout(() => confetti.remove(), 1000)

  if (foundCount.value === totalOlis.value) {
    gameWon.value = true
    stopTimer()
  }
}

const resetGame = () => {
  level.value = 1
  initGame()
}

const nextLevel = () => {
  level.value++
  initGame()
}

onMounted(() => {
  initGame()
})

onUnmounted(() => {
  stopTimer()
})
</script>

<style scoped>
.wo-ist-oli-container {
  padding: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  min-height: 100vh;
}

.game-area {
  position: relative;
  width: 100%;
  height: 600px;
  background: linear-gradient(to bottom, #87CEEB 0%, #98D8C8 100%);
  border-radius: 8px;
  overflow: hidden;
  border: 3px solid #333;
}

.person,
.oli {
  position: absolute;
  cursor: pointer;
  user-select: none;
  transition: transform 0.2s;
}

.person:hover,
.oli:not(.found):hover {
  transform: scale(1.2);
}

.oli.found {
  opacity: 0.3;
  cursor: default;
  filter: grayscale(100%);
}

.oli.pulse:not(.found) {
  animation: subtle-pulse 2s ease-in-out infinite;
}

@keyframes subtle-pulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

@keyframes float-up {
  0% {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
  100% {
    transform: translateY(-100px) scale(2);
    opacity: 0;
  }
}
</style>
